function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import ServiceFactory from "../../service";
import Auth from "../../functions/authenticate";
import { num, titleCase, resolveBusinessModel } from "../sales/territory-common";
import { AuthBase } from "../../../plunk/service/auth-base";
import endpoints from "../../endpoints";
import account from "../../data/account";
import PeopleShell, { PEOPLE_SUBTITLES } from "../people/shell";
import { getContacts, getActOn } from "../people/api";
import ContactsStrip from "./strip";
import { listAudiences, addMembers, getMembers, isSoft, toast } from "../audiences/api";

/*
 * Contacts list (People redesign 2026-10-09, PlunkDocs mockups/people-redesign-2026-10-09.html board 1).
 *
 * Data: /v1/people/contacts (search, stage pills, counts) and /v1/people/act-on (left card).
 * While those are not deployed (null from the client), the page falls back to the activity roster
 * /contacts/roster/{range} the page used before, so nothing breaks.
 * /Contacts/Audience/:audience shows the members of that list (audience members endpoint).
 * Ids above 2^53 arrive as strings: String() only, never Number().
 */

const PAGE = 25;
const SYSTEM_LISTS = ["all contacts", "customers"];
const STAGES = {
  lead: "Lead",
  prospect: "Prospect",
  customer: "Customer"
};
const AVATAR = {
  lead: ["#e6eefc", "#1d4ed8"],
  prospect: ["#fdf3e1", "#8a5a00"],
  customer: ["#e3f3e9", "#217a4b"]
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function rosterSvc(api) {
  const Service = ServiceFactory.getType(api);
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}

/** A real date, or null for empty, invalid or placeholder (pre-2000) values. */
export function asDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return null;
  return d;
}
function dayDiff(d) {
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const n = new Date();
  const b = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  return Math.round((b - a) / 86400000);
}
/** "Oct 5", or "Oct 5, 2024" outside the current year. */
export function monthDay(v) {
  const d = asDate(v);
  if (!d) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}${d.getFullYear() === new Date().getFullYear() ? "" : `, ${d.getFullYear()}`}`;
}
/** Short readable when: "Today", "Yesterday", "Tue" (this week), else "Sep 22". */
export function shortWhen(v) {
  const d = asDate(v);
  if (!d) return "";
  const diff = dayDiff(d);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return DAYS[d.getDay()];
  return monthDay(d);
}
export function initials(name) {
  return String(name || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "?";
}
export function stageKey(s) {
  const k = String(s || "").toLowerCase();
  return STAGES[k] ? k : null;
}
export function StageChip({
  stage
}) {
  const k = stageKey(stage);
  return k ? /*#__PURE__*/React.createElement("span", {
    className: `pc-stage ${k}`
  }, STAGES[k]) : null;
}
function displayName(name, email) {
  const n = String(name || "").trim();
  if (!n) return email || "No name yet";
  return n.indexOf("@") > -1 ? n : titleCase(n);
}

/**
 * "Add to a list" picker: static lists only (dynamic and system lists fill themselves).
 * people = [{ email, name }]. Uses the audience members endpoint, falling back to the older
 * x-audience-id contacts post when that endpoint is not deployed.
 */
export class ListPicker extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "add", async list => {
      const people = (this.props.people || []).filter(p => p && p.email);
      if (!people.length) {
        this.setState({
          err: "No email address on file, so they can't be added to a list yet."
        });
        return;
      }
      this.setState({
        busy: true,
        err: ""
      });
      try {
        try {
          await addMembers(list.id, {
            emails: people.map(p => p.email)
          });
        } catch (e) {
          if (!isSoft(e)) throw e;
          const Service = ServiceFactory.getType("/audience", "audience");
          Service.headers = [...(Service.headers || []).filter(h => String(h).indexOf("x-audience-id:") !== 0), "x-audience-id:" + String(list.id)];
          await new Service().postAsync(people.map(p => {
            const nm = String(p.name || "").indexOf("@") > -1 ? [] : String(p.name || "").trim().split(/\s+/).filter(Boolean);
            const row = {
              emailAddress: p.email
            };
            if (nm[0]) row.firstName = nm[0];
            if (nm.length > 1) row.lastName = nm.slice(1).join(" ");
            return row;
          }));
        }
        toast(people.length === 1 ? `Added ${people[0].name || people[0].email} to “${list.name}”.` : `Added ${num(people.length)} people to “${list.name}”.`);
        if (this._alive) this.setState({
          busy: false
        });
        this.props.onDone ? this.props.onDone(list) : this.props.onClose();
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          err: e.message || "Could not add them. Try again."
        });
      }
    });
    this.state = {
      lists: null,
      busy: false,
      err: ""
    };
    this._alive = true;
  }
  componentDidMount() {
    listAudiences().then(r => {
      const all = Array.isArray(r) ? r : r && r.items || [];
      const lists = all.filter(a => a.kind !== "dynamic" && a.kind !== "system" && a.kind !== "mirror" && !SYSTEM_LISTS.includes(String(a.name || "").toLowerCase()));
      if (this._alive) this.setState({
        lists
      });
    }).catch(() => {
      if (this._alive) this.setState({
        lists: []
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      lists,
      busy,
      err
    } = this.state;
    const people = this.props.people || [];
    const who = people.length === 1 ? people[0].name || people[0].email || "this person" : `${num(people.length)} people`;
    return /*#__PURE__*/React.createElement("div", {
      className: "pc-modal-back",
      onClick: e => {
        if (e.target === e.currentTarget && !busy) this.props.onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-modal",
      role: "dialog",
      "aria-label": "Add to a list"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-modal-title"
    }, "Add ", who, " to a list"), lists === null ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted"
    }, "Loading your lists\u2026") : lists.length === 0 ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted"
    }, "You don't have any lists you can add people to yet. Make one under Audiences.") : /*#__PURE__*/React.createElement("div", {
      className: "pc-modal-list"
    }, lists.map(a => /*#__PURE__*/React.createElement("button", {
      key: String(a.id),
      type: "button",
      className: "pc-modal-row",
      disabled: busy,
      onClick: () => this.add(a)
    }, /*#__PURE__*/React.createElement("span", null, a.name), a.total != null || a.count != null ? /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, num(a.total != null ? a.total : a.count), " people") : null))), err ? /*#__PURE__*/React.createElement("p", {
      className: "pc-err"
    }, err) : null, /*#__PURE__*/React.createElement("div", {
      className: "pc-modal-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: busy,
      onClick: this.props.onClose
    }, "Cancel"))));
  }
}
class ContactsListComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onDocDown", e => {
      if (this.state.exportMenu && this._exportEl && !this._exportEl.contains(e.target)) this.setState({
        exportMenu: false
      });
    });
    _defineProperty(this, "loadMore", async () => {
      if (this.state.loadingMore || !this.state.rows) return;
      const req = this._req;
      this.setState({
        loadingMore: true
      });
      try {
        const page = await this.fetchPage(this.state.rows.length);
        if (!this._alive || req !== this._req) return;
        this.setState({
          rows: [...this.state.rows, ...page.items],
          loadingMore: false
        });
      } catch (e) {
        if (this._alive) this.setState({
          loadingMore: false
        });
      }
    });
    _defineProperty(this, "onSearch", e => {
      const q = e.target.value;
      this.setState({
        q
      });
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.load(true), 350);
    });
    _defineProperty(this, "setFilter", filter => {
      if (filter === this.state.filter) return;
      this.setState({
        filter,
        selected: {}
      }, () => this.load(true));
    });
    _defineProperty(this, "toggle", row => {
      const selected = {
        ...this.state.selected
      };
      if (selected[row.id]) delete selected[row.id];else selected[row.id] = row;
      this.setState({
        selected
      });
    });
    _defineProperty(this, "toggleAll", () => {
      const rows = this.state.rows || [];
      const allOn = rows.length > 0 && rows.every(r => this.state.selected[r.id]);
      const selected = {
        ...this.state.selected
      };
      rows.forEach(r => {
        if (allOn) delete selected[r.id];else selected[r.id] = r;
      });
      this.setState({
        selected
      });
    });
    // Roster export: the server streams CSV with the roster's filter contract (audit-logged server-side).
    _defineProperty(this, "exportRoster", async scope => {
      if (this.state.exporting) return;
      this.setState({
        exporting: true,
        exportMenu: false
      });
      try {
        const range = scope === "all" ? "all_time" : this.rosterRange();
        const qs = scope === "all" ? "?includeInactive=true" : `?$orderby=lastseen${this.state.filter === "active" ? "" : "&includeInactive=true"}`;
        const res = await fetch(`${endpoints.activity.root}/contacts/roster/${range}/export${qs}`, {
          headers: {
            Authorization: "Bearer " + (AuthBase.idToken || ""),
            "x-app-id": (account.get() || {}).tenantId || ""
          }
        });
        if (!res.ok) throw new Error("HTTP " + res.status);
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = scope === "all" ? "contacts-all.csv" : `contacts-${range}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast("Your export has downloaded.");
      } catch (e) {
        toast("The export didn't work. Try again in a minute.");
      }
      if (this._alive) this.setState({
        exporting: false
      });
    });
    this.state = {
      q: (() => {
        try {
          return new URLSearchParams(window.location.search).get("q") || "";
        } catch (e) {
          return "";
        }
      })(),
      filter: "all",
      // ?q= from the toolbar search
      rows: null,
      count: 0,
      counts: null,
      source: null,
      error: false,
      loadingMore: false,
      actOn: undefined,
      list: undefined,
      // audience mode: the resolved list, or null when not found
      selected: {},
      // id -> row
      picker: null,
      // people for the list picker
      exportMenu: false,
      exporting: false,
      customersLabel: "Customers"
    };
    this._alive = true;
    this._req = 0;
  }
  audienceParam() {
    const a = this.props.params && this.props.params.audience;
    return a ? String(a).replace(/@/g, " ") : null;
  }
  async componentDidMount() {
    document.addEventListener("mousedown", this.onDocDown);
    resolveBusinessModel().then(bm => {
      if (this._alive) this.setState({
        customersLabel: bm === "retail" ? "Customers" : "Dealers"
      });
    }).catch(() => {});
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) {
      this.load(true);
      this.loadActOn();
    }
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._debounce);
    document.removeEventListener("mousedown", this.onDocDown);
  }
  componentDidUpdate(prev) {
    const pa = prev.params && prev.params.audience || "";
    const ca = this.props.params && this.props.params.audience || "";
    if (pa !== ca) this.setState({
      list: undefined,
      selected: {},
      q: "",
      filter: "all"
    }, () => this.load(true));
  }
  // ------------------------------------------------------------------ data

  async resolveList() {
    if (this.state.list !== undefined) return this.state.list;
    const want = this.audienceParam();
    const r = await safe(listAudiences(), []);
    const all = Array.isArray(r) ? r : r && r.items || [];
    const hit = all.find(a => String(a.id) === want) || all.find(a => String(a.name || "").toLowerCase() === String(want).toLowerCase()) || null;
    if (this._alive) this.setState({
      list: hit
    });
    return hit;
  }
  rosterRange() {
    return this.state.filter === "active" ? "last_7_days" : "all_time";
  }
  rosterQs(skip) {
    const {
      filter,
      q
    } = this.state;
    const order = filter === "matches" ? "match_score" : "lastseen";
    return `?$top=${PAGE}${skip ? `&$skip=${skip}` : ""}&$orderby=${order}${filter === "matches" ? "" : "&includeInactive=true"}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
  }
  async fetchPage(skip) {
    const {
      filter,
      q
    } = this.state;
    if (this.audienceParam()) {
      const list = await this.resolveList();
      if (!list) return {
        source: "list",
        items: [],
        count: 0
      };
      const r = await safe(getMembers(list.id, q || undefined, PAGE, skip), {
        items: [],
        count: 0
      });
      return {
        source: "list",
        count: Number(r.count) || 0,
        items: (r.items || []).map(m => ({
          id: String(m.personId),
          name: [m.firstName, m.lastName].filter(Boolean).join(" "),
          email: m.email,
          company: m.company,
          stage: m.lifecycleStage,
          lastSeen: m.lastActivityDate,
          visits: null,
          orders: null
        }))
      };
    }
    // A search always goes to the people search: the roster below ignores q (the Best matches ranking included).
    if (filter !== "matches" || q) {
      const query = {
        q: q || undefined,
        top: PAGE,
        skip: skip || undefined
      };
      if (STAGES[filter]) query.stage = filter;
      if (filter === "active") query.active = true;
      let r = null;
      try {
        r = await getContacts(query);
      } catch (e) {
        r = null;
      }
      if (r && Array.isArray(r.items)) {
        return {
          source: "people",
          items: r.items.map(x => ({
            ...x,
            id: String(x.id)
          })),
          count: Number(r.count) || 0,
          counts: r.counts || null
        };
      }
      // The search failed or timed out: say so, never the unfiltered roster dressed up as results.
      if (q) return {
        source: "people",
        items: [],
        count: 0,
        searchFailed: true
      };
    }
    // Fallback: the activity roster (also the source of the Best matches ranking).
    const roster = await safe(rosterSvc(`/contacts/roster/${this.rosterRange()}${this.rosterQs(skip)}`).getAsync(), {
      items: [],
      count: 0
    });
    return {
      source: "roster",
      count: Number(roster.count) || 0,
      items: (roster.items || []).map(r => ({
        id: String(r.id),
        name: r.name,
        email: r.email,
        company: r.company,
        stage: r.lifecycleStage,
        lastSeen: r.lastSeen,
        visits: r.sessions,
        orders: r.orders != null ? r.orders : null,
        why: (r.matchReasons || []).slice(0, 2).join(", ")
      }))
    };
  }
  async load(reset) {
    const req = ++this._req;
    if (reset) this.setState({
      rows: null,
      error: false
    });
    try {
      const page = await this.fetchPage(0);
      if (!this._alive || req !== this._req) return;
      this.setState(s => ({
        rows: page.items,
        count: page.count,
        source: page.source,
        searchFailed: !!page.searchFailed,
        counts: page.counts || (page.source === "roster" && s.filter === "all" && !s.q ? {
          all: page.count
        } : page.source === "people" ? s.counts : null)
      }));
    } catch (e) {
      if (this._alive && req === this._req) this.setState({
        error: true,
        rows: []
      });
    }
  }
  async loadActOn() {
    let r = null;
    try {
      r = await getActOn(6);
    } catch (e) {
      r = null;
    }
    if (r && Array.isArray(r.items)) {
      if (this._alive) this.setState({
        actOn: r.items.map(x => ({
          ...x,
          id: String(x.id)
        }))
      });
      return;
    }
    // Fallback: the top of the Best matches ranking, with its own reasons.
    const roster = await safe(rosterSvc(`/contacts/roster/last_7_days?$top=6&$orderby=match_score`).getAsync(), null);
    if (!this._alive) return;
    const items = (roster && roster.items || []).filter(x => (x.matchReasons || []).length).map(x => ({
      id: String(x.id),
      name: x.name || x.email,
      company: x.company,
      stage: x.lifecycleStage,
      reason: x.matchReasons.slice(0, 2).join(", "),
      at: x.lastSeen
    }));
    this.setState({
      actOn: items
    });
  }
  // --------------------------------------------------------------- render

  renderActOn() {
    const {
      actOn
    } = this.state;
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card pc-acton"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-card-head"
    }, /*#__PURE__*/React.createElement("b", null, "People to act on"), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, "this week")), actOn === undefined ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted pc-pad"
    }, "Finding the people worth a call\u2026") : !actOn.length ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted pc-pad"
    }, "Nobody stands out right now. As people visit, open your emails and order, the ones worth a follow-up show up here.") : actOn.map(p => {
      const k = stageKey(p.stage);
      const [bg, fg] = AVATAR[k] || ["#f3e8ff", "#6d28d9"];
      return /*#__PURE__*/React.createElement(Link, {
        key: p.id,
        className: "pc-acton-row",
        to: `/Contacts/Detail/${encodeURIComponent(p.id)}`
      }, /*#__PURE__*/React.createElement("span", {
        className: "pc-avatar",
        style: {
          background: bg,
          color: fg
        }
      }, initials(displayName(p.name))), /*#__PURE__*/React.createElement("span", {
        className: "pc-acton-main"
      }, /*#__PURE__*/React.createElement("span", {
        className: "pc-acton-top"
      }, /*#__PURE__*/React.createElement("b", null, displayName(p.name)), p.company ? /*#__PURE__*/React.createElement("span", {
        className: "pc-muted"
      }, p.company) : null, /*#__PURE__*/React.createElement(StageChip, {
        stage: p.stage
      })), p.reason ? /*#__PURE__*/React.createElement("span", {
        className: "pc-muted pc-reason"
      }, p.reason) : null), /*#__PURE__*/React.createElement("span", {
        className: "pc-muted pc-when"
      }, shortWhen(p.at)));
    }), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted pc-foot"
    }, "Picked from site visits, emails and orders. Updated every hour."));
  }
  renderPills() {
    const {
      filter,
      counts,
      source
    } = this.state;
    if (this.audienceParam()) return null;
    const c = counts || {};
    const pills = [["all", "Everyone", c.all], ["active", "Active this week", c.active]];
    // Stage pills need the People endpoint (the roster can't filter by stage).
    if (source === "people") {
      pills.push(["lead", "Leads", c.lead], ["prospect", "Prospects", c.prospect], ["customer", "Customers", c.customer]);
    }
    pills.push(["matches", "Best matches", null]);
    return pills.map(([k, label, n]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: "pc-pill" + (filter === k ? " on" : ""),
      onClick: () => this.setFilter(k),
      title: k === "lead" || k === "prospect" || k === "customer" ? "People, each at the furthest stage of the accounts they belong to. The Lifecycle tab counts accounts, so its numbers differ." : undefined
    }, label, n != null ? /*#__PURE__*/React.createElement("span", {
      className: "pc-pill-n"
    }, num(n)) : null));
  }
  renderTable() {
    const {
      rows,
      count,
      selected,
      loadingMore,
      filter
    } = this.state;
    if (rows === null) return /*#__PURE__*/React.createElement("p", {
      className: "pc-muted pc-pad"
    }, "Loading contacts\u2026");
    if (!rows.length) {
      return /*#__PURE__*/React.createElement("p", {
        className: "pc-muted pc-pad"
      }, this.state.searchFailed ? `The search for “${this.state.q}” didn’t finish. Try again in a moment.` : this.state.q ? `Nobody matches “${this.state.q}”. Try a name, an email address or a company.` : this.audienceParam() ? "Nobody is in this list yet." : filter === "active" ? "Nobody has been active this week yet." : "No contacts here yet. People show up once they visit your site, fill in a form or arrive through an import.");
    }
    const allOn = rows.every(r => selected[r.id]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "pc-tbl",
      role: "table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-tr pc-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      "aria-label": "Tick everyone shown",
      checked: allOn,
      onChange: this.toggleAll
    })), /*#__PURE__*/React.createElement("span", null, "Name"), /*#__PURE__*/React.createElement("span", null, "Company"), /*#__PURE__*/React.createElement("span", null, "Stage"), /*#__PURE__*/React.createElement("span", null, "Last seen"), /*#__PURE__*/React.createElement("span", {
      className: "r"
    }, "Visits"), /*#__PURE__*/React.createElement("span", {
      className: "r"
    }, "Engagements"), /*#__PURE__*/React.createElement("span", {
      className: "r"
    }, "Orders")), rows.map(r => {
      const name = displayName(r.name, r.email);
      const href = `/Contacts/Detail/${encodeURIComponent(r.id)}`;
      return /*#__PURE__*/React.createElement("div", {
        key: r.id,
        className: "pc-tr" + (selected[r.id] ? " on" : ""),
        role: "row",
        onClick: () => this.props.history.push(href)
      }, /*#__PURE__*/React.createElement("span", {
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        "aria-label": `Tick ${name}`,
        checked: !!selected[r.id],
        onChange: () => this.toggle(r)
      })), /*#__PURE__*/React.createElement("span", {
        className: "pc-name"
      }, /*#__PURE__*/React.createElement(Link, {
        to: href,
        onClick: e => e.stopPropagation()
      }, name), r.email && r.email !== name ? /*#__PURE__*/React.createElement("span", {
        className: "pc-muted"
      }, r.email) : null, filter === "matches" && r.why ? /*#__PURE__*/React.createElement("span", {
        className: "pc-muted"
      }, r.why) : null), /*#__PURE__*/React.createElement("span", {
        className: "pc-muted"
      }, r.company ? titleCase(r.company) : ""), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(StageChip, {
        stage: r.stage
      })), /*#__PURE__*/React.createElement("span", {
        className: "pc-muted"
      }, shortWhen(r.lastSeen)), /*#__PURE__*/React.createElement("span", {
        className: "r"
      }, r.visits != null ? num(r.visits) : ""), /*#__PURE__*/React.createElement("span", {
        className: "r"
      }, r.engagements != null ? num(r.engagements) : ""), /*#__PURE__*/React.createElement("span", {
        className: "r"
      }, r.orders != null ? num(r.orders) : ""));
    })), /*#__PURE__*/React.createElement("div", {
      className: "pc-tbl-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, "Showing ", num(rows.length), " of ", num(Math.max(count, rows.length)), " \xB7 tick people to add them to a list"), rows.length < count ? /*#__PURE__*/React.createElement(Button, {
      onClick: this.loadMore,
      disabled: loadingMore
    }, loadingMore ? "Loading…" : "Show more") : null));
  }
  render() {
    const {
      q,
      selected,
      picker,
      exportMenu,
      exporting,
      customersLabel,
      list
    } = this.state;
    const ticked = Object.values(selected);
    const listName = this.audienceParam();
    const actions = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.props.history.push("/Audience/Create")
    }, "Import contacts"));
    return /*#__PURE__*/React.createElement(PeopleShell, {
      subtitle: PEOPLE_SUBTITLES.contacts,
      actions: actions,
      cls: "pc-view",
      customersLabel: customersLabel
    }, /*#__PURE__*/React.createElement(ContactsStrip, null), /*#__PURE__*/React.createElement("div", {
      className: "pc-grid"
    }, this.renderActOn(), /*#__PURE__*/React.createElement("section", {
      className: "pc-card pc-roster"
    }, listName ? /*#__PURE__*/React.createElement("div", {
      className: "pc-listline"
    }, /*#__PURE__*/React.createElement("span", null, "People in the list ", /*#__PURE__*/React.createElement("b", null, list && list.name || listName), list === null ? " (we couldn't find this list)" : ""), /*#__PURE__*/React.createElement(Link, {
      to: "/Contacts"
    }, "Show everyone")) : null, /*#__PURE__*/React.createElement("div", {
      className: "pc-toolbar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-search"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons pc-search-ico",
      "aria-hidden": "true"
    }, "search"), /*#__PURE__*/React.createElement("input", {
      type: "search",
      value: q,
      onChange: this.onSearch,
      placeholder: "Search name, email or company",
      "aria-label": "Search contacts"
    })), /*#__PURE__*/React.createElement("span", {
      className: "pc-flex"
    }), /*#__PURE__*/React.createElement("span", {
      className: "pc-menu-wrap",
      ref: el => {
        this._exportEl = el;
      }
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: exporting,
      onClick: () => this.setState({
        exportMenu: !exportMenu
      })
    }, exporting ? "Exporting…" : "Export"), exportMenu ? /*#__PURE__*/React.createElement("div", {
      className: "pc-menu"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => this.exportRoster("view")
    }, /*#__PURE__*/React.createElement("b", null, "This view"), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, this.state.filter === "active" ? "People active this week" : "Everyone", ", with emails and phones")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => this.exportRoster("all")
    }, /*#__PURE__*/React.createElement("b", null, "All contacts"), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, "Everyone, no filters"))) : null)), /*#__PURE__*/React.createElement("div", {
      className: "pc-pillrow"
    }, this.renderPills()), ticked.length ? /*#__PURE__*/React.createElement("div", {
      className: "pc-selbar"
    }, /*#__PURE__*/React.createElement("span", null, ticked.length === 1 ? "1 person ticked" : `${num(ticked.length)} people ticked`), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      onClick: () => this.setState({
        picker: ticked.map(r => ({
          email: r.email,
          name: displayName(r.name, r.email)
        }))
      })
    }, "Add to a list"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        selected: {}
      })
    }, "Clear")) : null, this.state.error ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted pc-pad"
    }, "We couldn't load your contacts. Refresh the page to try again.") : this.renderTable())), picker ? /*#__PURE__*/React.createElement(ListPicker, {
      people: picker,
      onClose: () => this.setState({
        picker: null
      }),
      onDone: () => this.setState({
        picker: null,
        selected: {}
      })
    }) : null);
  }
}
export const ContactsViewComponent = withRouter(ContactsListComponent);
export default {
  ContactsViewComponent
};